import React from 'react';
import { pipelineSteps, pipelineLanes } from '../../mock';
import { CheckCircle2, Circle, Dot } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

export default function LoanPipeline() {
  const completedCount = pipelineSteps.filter((s) => s.status === 'complete').length;
  const currentIdx = pipelineSteps.findIndex((s) => s.status === 'current');
  const overallPct = Math.round(((completedCount + (currentIdx > -1 ? 0.5 : 0)) / pipelineSteps.length) * 100);

  return (
    <div className="rounded-2xl border hairline bg-white p-6 lg:p-7">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Loan Pipeline</div>
          <h2 className="display text-[22px] font-bold text-neutral-900 tracking-tight mt-0.5">Closing Disclosure Requested</h2>
          <p className="text-[12.5px] text-neutral-500 mt-1">
            CD has been issued. You may now request Closing Documents.
          </p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Overall</div>
            <div className="flex items-baseline gap-1.5 justify-end">
              <span className="display text-[26px] font-bold tracking-tight text-neutral-900">{overallPct}%</span>
              <span className="text-[11px] text-neutral-500">complete</span>
            </div>
          </div>
          <button onClick={() => toast.success('Closing documents requested')} className="btn-ink rounded-lg text-[12.5px] font-semibold px-4 py-2.5">
            Request Closing Documents
          </button>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-0 right-0 top-[22px] h-px bg-neutral-200" />
        <div
          className="absolute left-0 top-[22px] h-px bg-neutral-900"
          style={{ width: `${Math.max(0, (completedCount - 0.5) / (pipelineSteps.length - 1)) * 100}%` }}
        />

        <div className="grid" style={{ gridTemplateColumns: `repeat(${pipelineSteps.length}, minmax(0, 1fr))` }}>
          {pipelineSteps.map((step) => {
            const isComplete = step.status === 'complete';
            const isCurrent = step.status === 'current';
            return (
              <div key={step.key} className="flex flex-col items-center text-center px-1">
                <div
                  className={cn(
                    'relative h-11 w-11 rounded-full flex items-center justify-center border bg-white transition',
                    isComplete && 'border-neutral-900 bg-neutral-900',
                    isCurrent && 'border-[hsl(var(--kf-gold))] bg-white shadow-[0_0_0_6px_hsl(var(--kf-gold)/0.15)]',
                    !isComplete && !isCurrent && 'border-neutral-200'
                  )}
                >
                  {isComplete ? (
                    <CheckCircle2 className="h-5 w-5 text-[hsl(var(--kf-gold))]" strokeWidth={2.5} />
                  ) : isCurrent ? (
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-[hsl(var(--kf-gold))]/30 animate-ping" />
                      <Dot className="h-8 w-8 text-[hsl(var(--kf-gold))]" strokeWidth={4} />
                    </div>
                  ) : (
                    <Circle className="h-4 w-4 text-neutral-300" strokeWidth={2.5} />
                  )}
                </div>
                {step.date && (
                  <div
                    className={cn(
                      'mt-2 mono text-[9.5px] font-bold px-2 py-0.5 rounded',
                      isCurrent
                        ? 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))]'
                        : isComplete
                        ? 'bg-neutral-900 text-white'
                        : 'bg-neutral-100 text-neutral-400'
                    )}
                  >
                    {step.date}
                  </div>
                )}
                <div
                  className={cn(
                    'mt-2 text-[11px] leading-tight max-w-[110px]',
                    isCurrent ? 'text-neutral-900 font-semibold' : isComplete ? 'text-neutral-700 font-medium' : 'text-neutral-400'
                  )}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-7 pt-6 border-t hairline grid grid-cols-2 lg:grid-cols-4 gap-3">
        {pipelineLanes.map((lane) => (
          <div key={lane.title} className="rounded-xl border hairline p-4 card-lift">
            <div className="flex items-center justify-between">
              <span className="text-[11.5px] font-bold text-neutral-900">{lane.title}</span>
              <span className="mono text-[10px] font-bold text-neutral-900">{lane.completion}%</span>
            </div>
            <div className="text-[10.5px] text-neutral-500 mt-0.5">{lane.description}</div>
            <div className="mt-3 h-1 rounded-full bg-neutral-100 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full',
                  lane.accent === 'gold' && 'bg-[hsl(var(--kf-gold))]',
                  lane.accent === 'graphite' && 'bg-neutral-900',
                  lane.accent === 'emerald' && 'bg-emerald-500',
                  lane.accent === 'amber' && 'bg-amber-500'
                )}
                style={{ width: `${lane.completion}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
